import { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  RefreshCw, Eye, EyeOff, HelpCircle, Plus, Settings2, 
  ArrowLeftRight, Package, Truck, Tag
} from "lucide-react";

// Amazon TR Marketplace Component
export function AmazonSettings({ token, credentials, onSave }) {
  const [saving, setSaving] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [activeTab, setActiveTab] = useState("ayarlar");

  const [config, setConfig] = useState({
    // Yetkilendirme Bilgileri
    magazaId: credentials?.seller_id || "",
    accessToken: credentials?.api_key || "",
    refreshToken: credentials?.api_secret || "",
    tokenSonKullanmaTarihi: "",
    // Ürün Aktarım Ayarları
    urunAdi: "urun_adi",
    aciklama: "aciklama",
    markaBilgisi: "marka",
    satisFiyatTip: "satis_fiyati",
    aktifPasifUrunleriAktar: false,
    tlFiyataKomisyonEkle: false,
    fiyatGonderimi: true,
    komisyonTuru: "hicbiri",
    komisyonDegeri: 0,
    urunKoduAlani: "urun_kart_id_varyasyon_id",
    dil: "tr",
    rezervStok: 0,
    mense: "turkiye",
    kargoSuresiGun: 2,
    marketplaceGrup: "marketplace_grup_2",
    komisyonuUrundenAl: false,
    rezervStokuUrundenAl: false,
    // Barkod Muafiyeti Ayarları
    markaMuafiyeti: [],
    kategoriMuafiyeti: []
  });

  useEffect(() => {
    if (credentials?.extra_config) {
      setConfig(prev => ({ ...prev, ...credentials.extra_config }));
    }
  }, [credentials]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        marketplace: "amazon",
        seller_id: config.magazaId,
        api_key: config.accessToken,
        api_secret: config.refreshToken,
        extra_config: config
      });
      toast.success("Amazon ayarları kaydedildi");
    } catch (error) {
      toast.error("Ayarlar kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const handleReauthorize = () => {
    toast.info("Amazon OAuth yetkilendirme sayfasına yönlendiriliyorsunuz...");
    // TODO: Gerçek OAuth akışı
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between bg-white p-6 rounded-lg border">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-amber-500 rounded-xl flex items-center justify-center text-white font-bold text-3xl">
            a
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-800">Amazon TR</h2>
            <p className="text-slate-500">Dünyanın en büyük e-ticaret platformu</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <ArrowLeftRight className="w-4 h-4 mr-2" />
            Pazaryeri Değiştir
          </Button>
          <Button variant="outline" size="sm">
            <HelpCircle className="w-4 h-4 mr-2" />
            Yardım
          </Button>
          <Button variant="outline" size="sm">
            <Plus className="w-4 h-4 mr-2" />
            YENİ EŞLEŞTİRME YAP
          </Button>
          <Button variant="outline" size="sm">
            <Settings2 className="w-4 h-4 mr-2" />
            OTOMATİK EŞLEŞTİRME
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full justify-start bg-slate-100 p-1">
          <TabsTrigger value="ayarlar" className="flex-1">Ayarlar</TabsTrigger>
          <TabsTrigger value="urun-aktarimi" className="flex-1">Ürün Aktarımı / Güncelleme</TabsTrigger>
          <TabsTrigger value="etiket-yazdir" className="flex-1">Etiket Yazdır</TabsTrigger>
          <TabsTrigger value="fba-lojistik" className="flex-1">FBA - Lojistik</TabsTrigger>
        </TabsList>

        <TabsContent value="ayarlar" className="space-y-6 mt-6">
          {/* Yetkilendirme Bilgileri */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Yetkilendirme Bilgileri</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button onClick={handleReauthorize} className="bg-amber-500 hover:bg-amber-600 mb-4">
                <RefreshCw className="w-4 h-4 mr-2" />
                Yeniden Yetkilendir
              </Button>
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Mağaza ID *</Label>
                  <Input 
                    value={config.magazaId}
                    onChange={(e) => setConfig({...config, magazaId: e.target.value})}
                    placeholder="A2UN9GMZ4N8520"
                    disabled
                    className="bg-slate-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Access Token *</Label>
                  <div className="relative">
                    <Input 
                      type={showToken ? "text" : "password"}
                      value={config.accessToken}
                      onChange={(e) => setConfig({...config, accessToken: e.target.value})}
                      disabled
                      className="bg-slate-50"
                    />
                    <button 
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      onClick={() => setShowToken(!showToken)}
                    >
                      {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Refresh Token *</Label>
                  <div className="relative">
                    <Input 
                      type={showToken ? "text" : "password"}
                      value={config.refreshToken}
                      disabled
                      className="bg-slate-50"
                    />
                    <button 
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      onClick={() => setShowToken(!showToken)}
                    >
                      {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Token Son Kullanma Tarihi</Label>
                  <Input 
                    value={config.tokenSonKullanmaTarihi || "21.02.2026"}
                    disabled
                    className="bg-slate-50"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Ürün Aktarım Ayarları */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Ürün Aktarım Ayarları</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                {/* Kolon 1 */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Ürün Adı *</Label>
                    <Select value={config.urunAdi} onValueChange={(v) => setConfig({...config, urunAdi: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="urun_adi">Ürün Adı</SelectItem>
                        <SelectItem value="urun_kodu">Ürün Kodu</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Satış Fiyat Tip *</Label>
                    <Select value={config.satisFiyatTip} onValueChange={(v) => setConfig({...config, satisFiyatTip: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="satis_fiyati">Satış Fiyatı</SelectItem>
                        <SelectItem value="uye_tipi_fiyat_1">Üye Tipi Fiyat 1</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Komisyon Türü</Label>
                    <Select value={config.komisyonTuru} onValueChange={(v) => setConfig({...config, komisyonTuru: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hicbiri">Hiçbiri</SelectItem>
                        <SelectItem value="yuzde">Yüzde</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Dil *</Label>
                    <Select value={config.dil} onValueChange={(v) => setConfig({...config, dil: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tr">tr</SelectItem>
                        <SelectItem value="en">en</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Kargo Süresi (Gün)</Label>
                    <Input 
                      type="number"
                      value={config.kargoSuresiGun}
                      onChange={(e) => setConfig({...config, kargoSuresiGun: parseInt(e.target.value) || 0})}
                    />
                  </div>
                </div>

                {/* Kolon 2 */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Açıklama *</Label>
                    <Select value={config.aciklama} onValueChange={(v) => setConfig({...config, aciklama: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="aciklama">Açıklama</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 p-3 border rounded-lg">
                    <Checkbox 
                      checked={config.aktifPasifUrunleriAktar}
                      onCheckedChange={(c) => setConfig({...config, aktifPasifUrunleriAktar: c})}
                    />
                    <Label>Aktif ve Pasif Ürünleri Aktar</Label>
                  </div>
                  <div className="space-y-2">
                    <Label>Komisyon Değeri</Label>
                    <Input 
                      type="number"
                      value={config.komisyonDegeri}
                      onChange={(e) => setConfig({...config, komisyonDegeri: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Rezerv Stok</Label>
                    <Input 
                      type="number"
                      value={config.rezervStok}
                      onChange={(e) => setConfig({...config, rezervStok: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Marketplace Grup</Label>
                    <Select value={config.marketplaceGrup} onValueChange={(v) => setConfig({...config, marketplaceGrup: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="marketplace_grup_1">Marketplace Grup 1</SelectItem>
                        <SelectItem value="marketplace_grup_2">Marketplace Grup 2</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Kolon 3 */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Marka Bilgisi *</Label>
                    <Select value={config.markaBilgisi} onValueChange={(v) => setConfig({...config, markaBilgisi: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="marka">Marka</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 p-3 border rounded-lg">
                    <Checkbox 
                      checked={config.tlFiyataKomisyonEkle}
                      onCheckedChange={(c) => setConfig({...config, tlFiyataKomisyonEkle: c})}
                    />
                    <Label>TL Fiyata Komisyon Ekle</Label>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-amber-500 text-white rounded-lg">
                    <Checkbox 
                      checked={config.fiyatGonderimi}
                      onCheckedChange={(c) => setConfig({...config, fiyatGonderimi: c})}
                      className="border-white data-[state=checked]:bg-white data-[state=checked]:text-amber-500"
                    />
                    <Label className="text-white">Fiyat Gönderimi</Label>
                  </div>
                  <div className="space-y-2">
                    <Label>Ürün Kodu Alanı</Label>
                    <Select value={config.urunKoduAlani} onValueChange={(v) => setConfig({...config, urunKoduAlani: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="urun_kart_id_varyasyon_id">Ürün Kart ID - Varyasyon ID</SelectItem>
                        <SelectItem value="stok_kodu">Stok Kodu</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Menşe *</Label>
                    <Select value={config.mense} onValueChange={(v) => setConfig({...config, mense: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="turkiye">Türkiye</SelectItem>
                        <SelectItem value="cin">Çin</SelectItem>
                        <SelectItem value="almanya">Almanya</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 p-3 border rounded-lg">
                    <Checkbox 
                      checked={config.komisyonuUrundenAl}
                      onCheckedChange={(c) => setConfig({...config, komisyonuUrundenAl: c})}
                    />
                    <Label>Komisyonu Üründen Al</Label>
                  </div>
                  <div className="flex items-center gap-2 p-3 border rounded-lg">
                    <Checkbox 
                      checked={config.rezervStokuUrundenAl}
                      onCheckedChange={(c) => setConfig({...config, rezervStokuUrundenAl: c})}
                    />
                    <Label>Rezerv Stoğu Üründen Al</Label>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Barkod Muafiyeti Ayarları */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Barkod Muafiyeti Ayarları</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="marka">
                <TabsList>
                  <TabsTrigger value="marka" className="bg-amber-500 text-white data-[state=active]:bg-amber-600">Marka Muafiyeti</TabsTrigger>
                  <TabsTrigger value="kategori">Kategori Muafiyeti</TabsTrigger>
                </TabsList>
                <TabsContent value="marka" className="pt-4">
                  <div className="flex gap-2">
                    <Input placeholder="Marka" className="flex-1" />
                    <Button className="bg-amber-500 hover:bg-amber-600">EKLE</Button>
                  </div>
                  <div className="flex gap-2 mt-4 flex-wrap">
                    {config.markaMuafiyeti?.map((marka, i) => (
                      <Badge key={i} variant="outline">{marka} x</Badge>
                    ))}
                  </div>
                </TabsContent>
                <TabsContent value="kategori" className="pt-4">
                  <div className="flex gap-2">
                    <Input placeholder="Kategori" className="flex-1" />
                    <Button className="bg-amber-500 hover:bg-amber-600">EKLE</Button>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          <Button onClick={handleSave} disabled={saving} className="bg-amber-500 hover:bg-amber-600">
            {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
            Kaydet
          </Button>
        </TabsContent>

        <TabsContent value="urun-aktarimi">
          <Card>
            <CardContent className="pt-6">
              <p className="text-slate-500 text-center py-8">Ürün aktarım ve güncelleme özellikleri yakında eklenecek</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="etiket-yazdir">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <Tag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Etiket yazdırma özellikleri yakında eklenecek</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="fba-lojistik">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <Truck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">FBA - Lojistik özellikleri yakında eklenecek</p>
                <p className="text-sm text-slate-400 mt-2">Amazon Fulfillment (FBA) entegrasyonu</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// NOON Marketplace Component (UAE/KSA)
export function NoonSettings({ token, credentials, onSave }) {
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [activeTab, setActiveTab] = useState("ayarlar");

  const [config, setConfig] = useState({
    sellerId: credentials?.seller_id || "",
    apiKey: credentials?.api_key || "",
    apiSecret: credentials?.api_secret || "",
    region: "uae",
    warehouseCode: "",
    // Ürün Aktarım
    urunAdi: "urun_adi",
    aciklama: "aciklama",
    markaBilgisi: "marka",
    fiyatGonderimi: true,
    stokGonderimi: true,
    dil: "en",
    paraBirimi: "AED"
  });

  useEffect(() => {
    if (credentials?.extra_config) {
      setConfig(prev => ({ ...prev, ...credentials.extra_config }));
    }
  }, [credentials]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        marketplace: "noon",
        seller_id: config.sellerId,
        api_key: config.apiKey,
        api_secret: config.apiSecret,
        warehouse_code: config.warehouseCode,
        extra_config: config
      });
      toast.success("NOON ayarları kaydedildi");
    } catch (error) {
      toast.error("Ayarlar kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between bg-white p-6 rounded-lg border">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-yellow-400 rounded-xl flex items-center justify-center text-slate-900 font-bold text-xl">
            noon
          </div>
          <div>
            <h2 className="text-2xl font-bold text-yellow-600">NOON</h2>
            <p className="text-slate-500">Ortadoğu'nun lider e-ticaret platformu (UAE/KSA)</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <ArrowLeftRight className="w-4 h-4 mr-2" />
            Pazaryeri Değiştir
          </Button>
          <Button variant="outline" size="sm">
            <HelpCircle className="w-4 h-4 mr-2" />
            Yardım
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full justify-start bg-slate-100 p-1">
          <TabsTrigger value="ayarlar" className="flex-1">Ayarlar</TabsTrigger>
          <TabsTrigger value="urun-aktarimi" className="flex-1">Ürün Aktarımı</TabsTrigger>
          <TabsTrigger value="siparisler" className="flex-1">Siparişler</TabsTrigger>
        </TabsList>

        <TabsContent value="ayarlar" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">API Ayarları</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Seller ID *</Label>
                  <Input 
                    value={config.sellerId}
                    onChange={(e) => setConfig({...config, sellerId: e.target.value})}
                    placeholder="NOON Seller ID"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Region *</Label>
                  <Select value={config.region} onValueChange={(v) => setConfig({...config, region: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="uae">UAE (Birleşik Arap Emirlikleri)</SelectItem>
                      <SelectItem value="ksa">KSA (Suudi Arabistan)</SelectItem>
                      <SelectItem value="egypt">Egypt (Mısır)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>API Key *</Label>
                  <Input 
                    value={config.apiKey}
                    onChange={(e) => setConfig({...config, apiKey: e.target.value})}
                    placeholder="API Key"
                  />
                </div>
                <div className="space-y-2">
                  <Label>API Secret *</Label>
                  <div className="relative">
                    <Input 
                      type={showSecret ? "text" : "password"}
                      value={config.apiSecret}
                      onChange={(e) => setConfig({...config, apiSecret: e.target.value})}
                      placeholder="API Secret"
                    />
                    <button 
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                      onClick={() => setShowSecret(!showSecret)}
                    >
                      {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Warehouse Code</Label>
                <Input 
                  value={config.warehouseCode}
                  onChange={(e) => setConfig({...config, warehouseCode: e.target.value})}
                  placeholder="Depo kodu"
                />
              </div>
              <Button onClick={handleSave} disabled={saving} className="bg-yellow-500 hover:bg-yellow-600 text-slate-900">
                {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
                Kaydet
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Ürün Aktarım Ayarları</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Dil</Label>
                  <Select value={config.dil} onValueChange={(v) => setConfig({...config, dil: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="ar">العربية</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Para Birimi</Label>
                  <Select value={config.paraBirimi} onValueChange={(v) => setConfig({...config, paraBirimi: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AED">AED (Dirhem)</SelectItem>
                      <SelectItem value="SAR">SAR (Riyal)</SelectItem>
                      <SelectItem value="EGP">EGP (Mısır Lirası)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2 p-3 bg-yellow-400 rounded-lg">
                  <Checkbox 
                    checked={config.fiyatGonderimi}
                    onCheckedChange={(c) => setConfig({...config, fiyatGonderimi: c})}
                  />
                  <Label>Fiyat Gönderimi</Label>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="urun-aktarimi">
          <Card>
            <CardContent className="pt-6">
              <p className="text-slate-500 text-center py-8">Ürün aktarım özellikleri yakında eklenecek</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="siparisler">
          <Card>
            <CardContent className="pt-6">
              <p className="text-slate-500 text-center py-8">Sipariş yönetimi yakında eklenecek</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// NAMSHI Marketplace Component
export function NamshiSettings({ token, credentials, onSave }) {
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [activeTab, setActiveTab] = useState("ayarlar");

  const [config, setConfig] = useState({
    partnerId: credentials?.seller_id || "",
    apiKey: credentials?.api_key || "",
    region: "uae",
    // Ürün ayarları
    dil: "en",
    paraBirimi: "AED",
    fiyatGonderimi: true,
    stokGonderimi: true
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        marketplace: "namshi",
        seller_id: config.partnerId,
        api_key: config.apiKey,
        extra_config: config
      });
      toast.success("NAMSHI ayarları kaydedildi");
    } catch (error) {
      toast.error("Ayarlar kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between bg-white p-6 rounded-lg border">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-purple-600 rounded-xl flex items-center justify-center text-white font-bold text-xl">
            NA
          </div>
          <div>
            <h2 className="text-2xl font-bold text-purple-700">NAMSHI</h2>
            <p className="text-slate-500">Ortadoğu'nun moda e-ticaret platformu</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <ArrowLeftRight className="w-4 h-4 mr-2" />
            Pazaryeri Değiştir
          </Button>
          <Button variant="outline" size="sm">
            <HelpCircle className="w-4 h-4 mr-2" />
            Yardım
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full justify-start bg-slate-100 p-1">
          <TabsTrigger value="ayarlar" className="flex-1">Ayarlar</TabsTrigger>
          <TabsTrigger value="urun-aktarimi" className="flex-1">Ürün Aktarımı</TabsTrigger>
        </TabsList>

        <TabsContent value="ayarlar" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">API Ayarları</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Partner ID *</Label>
                  <Input 
                    value={config.partnerId}
                    onChange={(e) => setConfig({...config, partnerId: e.target.value})}
                    placeholder="NAMSHI Partner ID"
                  />
                </div>
                <div className="space-y-2">
                  <Label>API Key *</Label>
                  <div className="relative">
                    <Input 
                      type={showSecret ? "text" : "password"}
                      value={config.apiKey}
                      onChange={(e) => setConfig({...config, apiKey: e.target.value})}
                      placeholder="API Key"
                    />
                    <button 
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                      onClick={() => setShowSecret(!showSecret)}
                    >
                      {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Region</Label>
                <Select value={config.region} onValueChange={(v) => setConfig({...config, region: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="uae">UAE</SelectItem>
                    <SelectItem value="ksa">KSA</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
                {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
                Kaydet
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Aktarım Ayarları</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Dil</Label>
                  <Select value={config.dil} onValueChange={(v) => setConfig({...config, dil: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="ar">العربية</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Para Birimi</Label>
                  <Select value={config.paraBirimi} onValueChange={(v) => setConfig({...config, paraBirimi: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AED">AED</SelectItem>
                      <SelectItem value="SAR">SAR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="urun-aktarimi">
          <Card>
            <CardContent className="pt-6">
              <p className="text-slate-500 text-center py-8">Ürün aktarım özellikleri yakında eklenecek</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default { AmazonSettings, NoonSettings, NamshiSettings };
